import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {SearchBar} from 'shells/search-bar/search-bar';
import {ModalBody} from 'shells/typography/modal-body';
import {ModalHeader} from 'shells/typography/modal-header';

import {analyticsClient} from '../../../../analytics-client';
import type {AnalyticsConstant} from '../../../../analytics-client';

import {FORM_ADD_ON_ID} from './consts';
import {FormSelectionTable} from './forms/form-selection-table';
import {ModalEmptyState} from './modal-empty-state';
import {MeetingSelectionTable} from './scheduler/meeting-selection-table';

import styles from './custom-add-on-select-modal.module.css';

export type BeeResolveFunction = (arg1: {name: string; value: string}) => void;
export type BeeRejectFunction = () => void;

export type SelectedItem = {
    name: string;
    value: string;
};
export type SpecialState = 'loading' | 'empty';

export type CustomAddOn = 'nutshell-form' | 'nutshell-scheduler';

type Props = {
    addOnType: CustomAddOn;
    resolve: BeeResolveFunction;
    reject: BeeRejectFunction;
};

export function CustomAddOnSelectModal(props: Props) {
    const {resolve, reject} = props;

    const [selectedItem, setSelectedItem] = React.useState<SelectedItem | null | undefined>(
        undefined
    );
    const [search, setSearch] = React.useState<string>('');

    const [specialState, setSpecialState] = React.useState<SpecialState | null | undefined>(
        'loading'
    );

    // Currently only forms are searchable
    const isSearchable = props.addOnType === FORM_ADD_ON_ID;

    const getHeaderText = (): string => {
        let noun;
        if (props.addOnType === FORM_ADD_ON_ID) {
            noun = 'form';
        } else {
            noun = 'scheduler';
        }

        return `Add a ${noun} to this page`;
    };

    const getBody = (): string | React.ReactNode => {
        if (props.addOnType === FORM_ADD_ON_ID) {
            return 'Select which form you’d like to place in this page:';
        } else {
            return (
                <>
                    Choose from any of your active {/* @ts-expect-error */}
                    <Link as={RoutedLink} newTab={true} variant='primary' to='/scheduler'>
                        Scheduler
                    </Link>{' '}
                    meetings and add it to this page
                </>
            );
        }
    };

    const getSelectionTable = (): React.ReactElement => {
        if (props.addOnType === FORM_ADD_ON_ID) {
            return (
                <FormSelectionTable
                    search={search}
                    setSelectedItem={setSelectedItem}
                    selectedItem={selectedItem}
                    setSpecialState={setSpecialState}
                />
            );
        } else {
            return (
                <MeetingSelectionTable
                    setSelectedItem={setSelectedItem}
                    selectedItem={selectedItem}
                    setSpecialState={setSpecialState}
                />
            );
        }
    };

    const getAddButtonText = (): string => {
        if (props.addOnType === FORM_ADD_ON_ID) {
            return 'Add';
        } else {
            return 'Add meeting';
        }
    };

    const getAnalyticEvent = (): AnalyticsConstant => {
        if (props.addOnType === FORM_ADD_ON_ID) {
            return analyticsClient.EVENTS.TRACK_LANDING_PAGE_FORM_EMBEDDED;
        } else {
            return analyticsClient.EVENTS.TRACK_LANDING_PAGE_SCHEDULER_EMBEDDED;
        }
    };

    return (
        <Modal isOpen={true} onClose={reject} minWidth={1000}>
            <div className={styles.container}>
                <div>
                    <ModalHeader>{getHeaderText()}</ModalHeader>
                    {!specialState && <ModalBody>{getBody()}</ModalBody>}
                </div>
                {!specialState && isSearchable && (
                    <SearchBar onChange={setSearch} value={search} hasBorder={true} />
                )}
                {specialState === 'loading' ? <LoadingPage padding={0} /> : undefined}
                {specialState === 'empty' ? (
                    <ModalEmptyState variant={props.addOnType} />
                ) : undefined}
                <div
                    className={
                        specialState === 'loading'
                            ? styles['table-container--loading']
                            : styles['table-container']
                    }
                >
                    {getSelectionTable()}
                </div>
            </div>
            <FooterBar
                left={
                    <Button variant='text-secondary' size='big' onClick={reject} noPadding={true}>
                        Cancel
                    </Button>
                }
                right={
                    <Button
                        variant='save'
                        size='big'
                        onClick={() => {
                            if (selectedItem && selectedItem.name && selectedItem.value) {
                                analyticsClient.track(getAnalyticEvent());
                                resolve({
                                    name: selectedItem.name,
                                    value: selectedItem.value,
                                });
                            }
                        }}
                        disabled={!selectedItem}
                    >
                        {getAddButtonText()}
                    </Button>
                }
                absolutePosition={true}
            />
        </Modal>
    );
}
